// @flow
import React from 'react';
import { Col } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import messages from './messages';

type Props = {
  isADA: boolean,
  // Optional: the component defaults it, and callers with nothing to list omit it.
  accessibilityTypes?: Array<string>,
};

// Same placeholder the rest of the app uses for an unavailable value.
const NOT_APPLICABLE = '---';

// Typography renders a <span> for every variant here, so label and value would
// run together on one line ("ADAYes"). component="div" puts each on its own
// line, giving the label-above-value stack the rest of the form already uses.
const LABEL_PROPS = {
  component: 'div',
  fontWeight: 'bold',
  variant: 'inputLabel',
};

const VALUE_PROPS = { component: 'div' };

// The label/value gap comes from the Stack's spacing scale, not from a margin
// on either Typography — one step on the theme's 8px scale, the same idiom the
// rest of the app uses. Changing the scale centrally moves this row with it.
const LABEL_VALUE_SPACING = 1;

// The row's own vertical spacing, on the same scale: clear separation from the
// selectors above, and enough below that the values do not crowd the Issue
// Details heading. Steps on the scale, not hand-picked pixels.
const ROW_PADDING = { paddingTop: 5, paddingBottom: 3 };

/**
 * ADA status and accessibility types for the unit selected on a work order.
 *
 * Laid out as its own row under the unit selectors, with each value aligned to
 * the dropdown it describes — ADA under the household field, the type list
 * under the unit location field. Read-only text rather than chips or badges, so
 * it reads as part of the form, and both values are spelled out as words so the
 * meaning never depends on colour.
 *
 * The caller decides whether there is anything to show — with no unit selected
 * this is not rendered at all, rather than rendered with placeholder values.
 */
const UnitAccessibility = ({ isADA, accessibilityTypes = [] }: Props) => {
  // The API already sorts these, but the order is part of the requirement, so
  // it is not left to the caller to preserve. Joined into one line rather than
  // listed, matching how the management tables render the same values.
  const types = [...accessibilityTypes]
    // Falsy entries are dropped before sorting: localeCompare would throw on a
    // null, and the shared ada-helpers formatter guards the same way.
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');

  return (
    // clearfix drops this below the floated selector columns above, so the top
    // padding is measured from the bottom of that row rather than from inside
    // it. The column widths mirror those selectors exactly, which is what keeps
    // each value under the dropdown it describes.
    <Box
      className="clearfix"
      sx={ROW_PADDING}
      data-test="wo-unit-accessibility"
    >
      <Col xs={12} md={5} mdOffset={3}>
        <Stack spacing={LABEL_VALUE_SPACING}>
          <Typography {...LABEL_PROPS}>
            <FormattedMessage {...messages.ada} />
          </Typography>
          <Typography {...VALUE_PROPS} data-test="wo-unit-ada">
            {isADA ? (
              <FormattedMessage {...messages.yes} />
            ) : (
              <FormattedMessage {...messages.no} />
            )}
          </Typography>
        </Stack>
      </Col>
      <Col xs={12} md={4}>
        <Stack spacing={LABEL_VALUE_SPACING}>
          <Typography {...LABEL_PROPS}>
            <FormattedMessage {...messages.accessibilityType} />
          </Typography>
          <Typography {...VALUE_PROPS} data-test="wo-unit-accessibility-types">
            {types || NOT_APPLICABLE}
          </Typography>
        </Stack>
      </Col>
    </Box>
  );
};

export default UnitAccessibility;
